import { calculateGstBreakdown } from "../../../../utilis/gstCalculation";
import { formatCurrency } from "./commissionDetailConstants";
import {
  aggregateInstallmentDiscountMetrics,
  sumCommissionStudentDiscountFromInvoices,
} from "./commissionDiscountUtils";
import {
  calculateCommissionGstViews,
  DEFAULT_COMMISSION_GST_PERCENT,
  isBonusFromCommissionableEnabled,
  resolveBonusDeductionExGst,
  resolveEffectiveCommissionableTuitionFee,
} from "./commissionPlanUtils";

function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function roundMoney(value) {
  return Math.round(toNumber(value) * 100) / 100;
}

function pick(obj, keys = []) {
  if (!obj || typeof obj !== "object") return null;
  for (const key of keys) {
    if (obj[key] != null && obj[key] !== "") return obj[key];
  }
  return null;
}

function normalizeGstType(value) {
  const text = String(value || "").toLowerCase();
  if (text === "inclusive") return "inclusive";
  if (text === "exclusive") return "exclusive";
  return "notax";
}

function resolvePlanGstType(plan = {}) {
  return normalizeGstType(
    plan.commissionGstType ?? plan.commission_gst_type ?? "exclusive",
  );
}

/** Ex-GST base → ex GST + exclusive total + inclusive total. */
export function buildGstAmountViews(
  amountExGst,
  commissionGstType = "exclusive",
  gstPercentage = DEFAULT_COMMISSION_GST_PERCENT,
) {
  const exGst = roundMoney(Math.max(toNumber(amountExGst), 0));
  const taxType = normalizeGstType(commissionGstType);

  if (exGst <= 0 || taxType === "notax") {
    return {
      exGst,
      exclusiveTotal: exGst,
      inclusiveTotal: exGst,
      withGst: exGst,
    };
  }

  const exclusive = calculateGstBreakdown({
    baseCost: exGst,
    gstPercentage,
    taxType: "exclusive",
  });
  const inclusive = calculateGstBreakdown({
    baseCost: exGst,
    gstPercentage,
    taxType: "inclusive",
  });

  const exclusiveTotal = roundMoney(exclusive.professional_cost);
  const inclusiveTotal = roundMoney(inclusive.base_cost);

  return {
    exGst,
    exclusiveTotal,
    inclusiveTotal,
    withGst:
      taxType === "exclusive"
        ? exclusiveTotal
        : taxType === "inclusive"
          ? inclusiveTotal
          : exGst,
  };
}

/**
 * Plan setup card columns: Exclusive = ex-GST, Inclusive = GST-inclusive total.
 * (Inclusive column uses exclusive GST breakdown on the ex-GST base.)
 */
export function commissionPlanGstPair(
  amountExGst,
  commissionGstType = "exclusive",
  gstPercentage = DEFAULT_COMMISSION_GST_PERCENT,
) {
  const views = buildGstAmountViews(
    amountExGst,
    commissionGstType,
    gstPercentage,
  );
  return {
    exclusive: views.exGst,
    inclusive: views.exclusiveTotal,
  };
}

function formatGstRow(amountExGst, plan, currency) {
  const gstType = resolvePlanGstType(plan);
  const views = buildGstAmountViews(amountExGst, gstType);
  return {
    exGst: formatCurrency(views.exGst, currency),
    exclusive: formatCurrency(views.exclusiveTotal, currency),
    inclusive: formatCurrency(views.inclusiveTotal, currency),
    withGst: formatCurrency(views.withGst, currency),
    rawExGst: views.exGst,
  };
}

function resolvePlanTaxLabels(plan = {}) {
  const gstType = resolvePlanGstType(plan);
  if (gstType === "inclusive") {
    return {
      badge: "Inclusive GST",
      exColumn: "Ex GST",
      exclusiveColumn: "Exclusive",
      inclusiveColumn: "Inclusive",
      withGstColumn: "Incl. GST",
      hint: "Amounts shown ex GST and with GST (inclusive treatment).",
    };
  }
  if (gstType === "exclusive") {
    return {
      badge: "Exclusive GST",
      exColumn: "Ex GST",
      exclusiveColumn: "Exclusive (+GST)",
      inclusiveColumn: "Inclusive",
      withGstColumn: "Excl. GST (+GST)",
      hint: "Ex GST is before tax; exclusive column adds GST on top.",
    };
  }
  return {
    badge: "No tax",
    exColumn: "Amount",
    exclusiveColumn: "Amount",
    inclusiveColumn: "Amount",
    withGstColumn: "Amount",
    hint: "No GST on this commission plan.",
  };
}

/** API may return `{ summary: {...} }` or the summary object directly. */
export function normalizeCommissionPlanSummaryPayload(payload) {
  if (!payload || typeof payload !== "object") return null;
  if (payload.summary && typeof payload.summary === "object") {
    return payload.summary;
  }
  if (
    payload.tuition ||
    payload.commission ||
    payload.totals ||
    payload.bonus
  ) {
    return payload;
  }
  return null;
}

/** Client-side fallback when summary API is unavailable. */
export function buildPlanSummaryFromInstallments(installments = [], plan = {}) {
  const totals = aggregateInstallmentDiscountMetrics(installments, plan);
  const planDiscountAmount = toNumber(
    plan?.studentDiscountAmount ?? plan?.student_discount_amount,
  );
  let tuitionPlanned = toNumber(
    plan?.commissionableTuitionFee ?? plan?.commissionable_tuition_fee,
  );
  if (tuitionPlanned <= 0) {
    tuitionPlanned = totals.totalCommissionableFee;
  }

  return {
    currency: plan?.currency || "AUD",
    tuition: {
      planned_fee: tuitionPlanned,
      student_paid: totals.totalPaidFee,
      student_discount: Math.max(
        totals.totalStudentDiscount,
        planDiscountAmount,
      ),
      discount_paid: totals.totalDiscountPaid,
      balance_fee: totals.totalBalanceFee,
    },
    commission: {
      expected: totals.totalExpectedCommission,
      invoiced: 0,
      paid: roundMoney(
        (installments || []).reduce(
          (sum, row) =>
            sum + toNumber(row.commissionReceived ?? row.commission_received),
          0,
        ),
      ),
      due: totals.totalBalanceCommission,
    },
    totals: {
      student_discount: Math.max(
        totals.totalStudentDiscount,
        planDiscountAmount,
      ),
      discount_paid: totals.totalDiscountPaid,
      discount_balance: roundMoney(
        Math.max(
          totals.totalDiscountBalance,
          Math.max(totals.totalStudentDiscount, planDiscountAmount) -
            totals.totalDiscountPaid,
        ),
      ),
      net_commission: totals.totalNetCommission,
      balance_commission: totals.totalBalanceCommission,
    },
  };
}

export function resolvePlanPaymentSummary({
  apiSummary = null,
  installments = [],
  plan = {},
} = {}) {
  const normalized = normalizeCommissionPlanSummaryPayload(apiSummary);
  if (normalized) {
    return {
      ...normalized,
      currency: normalized.currency || plan?.currency || "AUD",
    };
  }
  if ((installments || []).length) {
    return buildPlanSummaryFromInstallments(installments, plan);
  }
  return null;
}

/**
 * Payment summary columns: professional fee → discount → commission → bonus → inquiry fees.
 * Each column shows simple amounts only (no Ex/Inc GST split).
 */
export function buildUnifiedPaymentLedger({
  tuitionSteps = [],
  discountSteps = [],
  commissionSteps = [],
  bonusSteps = null,
  inquiryFeeSteps = [],
  commissionHeader = null,
} = {}) {
  const resolveAmount = (row = {}) =>
    row.amount ??
    row.withGst ??
    row.exclusive ??
    row.inclusive ??
    row.exGst ??
    null;

  const toColumn = (id, title, steps = []) => {
    if (!Array.isArray(steps) || !steps.length) return null;
    return {
      id,
      title,
      lines: steps.map((row, index) => ({
        step: index + 1,
        label: row.label,
        type: row.type || "line",
        highlight: Boolean(row.highlight),
        amount: resolveAmount(row),
        sublabel: row.sublabel || "",
      })),
    };
  };

  const columns = [
    toColumn("tuition", "Commissionable Fee", tuitionSteps),
    toColumn("discount", "Student discount", discountSteps),
    toColumn("commission", "Commission", commissionSteps),
    toColumn("bonus", "Bonus", bonusSteps),
    toColumn("inquiry_fees", "Application & material fees", inquiryFeeSteps),
  ].filter(Boolean);

  const commissionColumn = columns.find((column) => column.id === "commission");
  if (commissionColumn && commissionHeader?.amount) {
    commissionColumn.balanceLabel =
      commissionHeader.label || "Expected commission";
    commissionColumn.balanceAmount = commissionHeader.amount;
  }

  return { columns };
}

function resolveCommissionExpectedExGst({
  plan = {},
  commission = {},
  calculatedExGst = 0,
  calculatedWithGst = 0,
}) {
  const fromPlan = toNumber(
    plan?.expectedCommissionExGst ?? plan?.expected_commission_ex_gst,
  );
  if (fromPlan > 0) return fromPlan;

  const calculated = roundMoney(Math.max(toNumber(calculatedExGst), 0));
  if (calculated > 0) return calculated;

  const fromApiExGst = toNumber(
    pick(commission, ["expected_ex_gst", "expectedExGst", "ex_gst"]),
  );
  if (fromApiExGst > 0) return fromApiExGst;

  const fromApi = toNumber(
    pick(commission, ["expected", "total", "amount"]),
  );
  if (fromApi <= 0) return 0;

  const withGst = roundMoney(Math.max(toNumber(calculatedWithGst), 0));
  if (withGst > 0 && calculated > 0 && Math.abs(fromApi - withGst) < 0.02) {
    return calculated;
  }

  if (calculated > 0 && fromApi > calculated) {
    return calculated;
  }

  return roundMoney(fromApi);
}

/**
 * Structured view model for the payment summary UI (sections + GST columns).
 */
export function buildPlanPaymentSummaryViewModel(
  summary,
  plan = {},
  currency = "AUD",
  options = {},
) {
  if (!summary || typeof summary !== "object") return null;

  const tuition = summary.tuition || {};
  const commission = summary.commission || {};
  const totals = summary.totals || {};
  const bonus = summary.bonus || {};

  const displayCurrency =
    summary.currency || plan?.currency || currency || "AUD";
  const taxLabels = resolvePlanTaxLabels(plan);
  const gstType = resolvePlanGstType(plan);

  const commissionableTuitionFee = (() => {
    const fromPlan = toNumber(
      plan?.commissionableTuitionFee ?? plan?.commissionable_tuition_fee,
    );
    if (fromPlan > 0) return fromPlan;

    const fromSummary = toNumber(
      pick(tuition, [
        "planned_fee",
        "commissionable_fee",
        "commissionable_tuition_fee",
      ]),
    );
    if (fromSummary > 0) return fromSummary;

    return 0;
  })();
  const activeBonus = options?.bonus || null;
  const tuitionFeeForBonus = toNumber(plan?.tuitionFee ?? plan?.tuition_fee);
  const bonusDeductionExGst = resolveBonusDeductionExGst({
    plan,
    bonus: activeBonus,
    tuitionFee: tuitionFeeForBonus,
    commissionableTuitionFee,
  });
  const effectiveCommissionableTuitionFee = resolveEffectiveCommissionableTuitionFee(
    {
      plan,
      bonus: activeBonus,
      tuitionFee: tuitionFeeForBonus,
      commissionableTuitionFee,
    },
  );
  const commissionableBaseForCommission = isBonusFromCommissionableEnabled(plan)
    ? effectiveCommissionableTuitionFee
    : commissionableTuitionFee;
  const tuitionPaid = toNumber(
    pick(tuition, ["student_paid", "paid", "paid_amount"]),
  );
  const remainingTuition = roundMoney(
    Math.max(
      toNumber(tuition.balance_fee),
      commissionableBaseForCommission > 0
        ? commissionableBaseForCommission - tuitionPaid
        : 0,
    ),
  );

  const planDiscountAmount = toNumber(
    plan?.studentDiscountAmount ?? plan?.student_discount_amount,
  );
  const studentDiscount = Math.max(
    toNumber(
      pick(totals, ["student_discount"]) ?? pick(tuition, ["student_discount"]),
    ),
    planDiscountAmount,
  );
  const discountPaid = toNumber(
    pick(totals, ["discount_paid"]) ?? pick(tuition, ["discount_paid"]),
  );
  const discountBalance = roundMoney(
    Math.max(toNumber(totals.discount_balance), studentDiscount - discountPaid),
  );

  const planGstViews = calculateCommissionGstViews({
    tuitionFee: tuitionFeeForBonus,
    commissionableTuitionFee: commissionableBaseForCommission,
    commissionType:
      plan?.commissionType || plan?.commission_type || "percentage",
    commissionRate: plan?.commissionRate ?? plan?.commission_rate,
    commissionGstType: gstType,
  });

  const expectedExGst = planGstViews.baseExGst;
  const expectedExclusive = planGstViews.exclusiveTotal;
  const expectedInclusive = planGstViews.inclusiveTotal;
  const expectedWithGst =
    gstType === "exclusive"
      ? expectedExclusive
      : gstType === "inclusive"
        ? expectedInclusive
        : expectedExGst;

  const commissionReceived = toNumber(
    pick(commission, ["paid", "received", "paid_amount"]),
  );

  const installmentRows = Array.isArray(options?.installments)
    ? options.installments
    : [];
  const installmentExpectedCommission = roundMoney(
    installmentRows.reduce(
      (sum, row) =>
        sum + toNumber(row.commissionAmount ?? row.commission_amount),
      0,
    ),
  );

  let commissionExpectedDisplay = resolveCommissionExpectedExGst({
    plan,
    commission,
    calculatedExGst: expectedExGst,
    calculatedWithGst: expectedWithGst,
  });
  if (installmentExpectedCommission > 0) {
    commissionExpectedDisplay = installmentExpectedCommission;
  }

  const commissionInvoices = Array.isArray(options?.commissionInvoices)
    ? options.commissionInvoices
    : [];
  const commissionDiscountFromInvoices =
    sumCommissionStudentDiscountFromInvoices(commissionInvoices);
  const commissionDiscountPaid =
    commissionDiscountFromInvoices > 0
      ? commissionDiscountFromInvoices
      : toNumber(
          pick(totals, [
            "commission_discount_paid",
            "commission_discount",
            "commission_discount_from_commission",
          ]) ??
            pick(commission, [
              "discount_from_commission",
              "commission_discount",
            ]),
        );
  const discountFromReceivedCommission = roundMoney(
    Math.min(commissionDiscountPaid, commissionReceived),
  );
  const netCommissionReceived = roundMoney(
    Math.max(commissionReceived - discountFromReceivedCommission, 0),
  );
  const outstandingCommission = roundMoney(
    Math.max(commissionExpectedDisplay - commissionReceived, 0),
  );
  const expectedCommissionAfterDiscount = roundMoney(
    Math.max(commissionExpectedDisplay - studentDiscount, 0),
  );

  const inquiryFees = summary.inquiry_fees || {};
  const applicationFeePlanned = toNumber(
    pick(inquiryFees.application_fee || {}, ["planned", "expected"]),
  );
  const applicationFeeInvoiced = toNumber(
    pick(inquiryFees.application_fee || {}, ["invoiced"]),
  );
  const applicationFeePaid = toNumber(
    pick(inquiryFees.application_fee || {}, ["paid"]),
  );
  const materialFeePlanned = toNumber(
    pick(inquiryFees.material_fee || {}, ["planned", "expected"]),
  );
  const materialFeeInvoiced = toNumber(
    pick(inquiryFees.material_fee || {}, ["invoiced"]),
  );
  const materialFeePaid = toNumber(
    pick(inquiryFees.material_fee || {}, ["paid"]),
  );

  const bonusExpected = toNumber(pick(bonus, ["expected", "total", "amount"]));
  const bonusReceived = toNumber(
    pick(bonus, ["paid", "received", "paid_amount"]),
  );

  const makeSimpleRow = (label, amount, options = {}) => ({
    label,
    amount: formatCurrency(amount, displayCurrency),
    sublabel: options.sublabel || "",
    highlight: options.highlight || false,
    type: options.type || "line",
    step: options.step ?? null,
  });

  const balanceFee = roundMoney(
    Math.max(toNumber(tuition.balance_fee), remainingTuition),
  );

  const tuitionSteps = (() => {
    const deductBonus =
      isBonusFromCommissionableEnabled(plan) && bonusDeductionExGst > 0;
    if (!deductBonus) {
      return [
        makeSimpleRow("Commissionable fee", commissionableTuitionFee, {
          step: 1,
          type: "open",
        }),
        makeSimpleRow("Less: Paid", tuitionPaid, {
          step: 2,
          type: "deduct",
        }),
        makeSimpleRow("Fee balance", balanceFee, {
          step: 3,
          type: "total",
          highlight: true,
        }),
      ];
    }

    return [
      makeSimpleRow("Commissionable fee", commissionableTuitionFee, {
        step: 1,
        type: "open",
      }),
      makeSimpleRow("Less: Bonus", bonusDeductionExGst, {
        step: 2,
        type: "deduct",
      }),
      makeSimpleRow("Net commissionable fee", effectiveCommissionableTuitionFee, {
        step: 3,
        type: "subtotal",
      }),
      makeSimpleRow("Less: Paid", tuitionPaid, {
        step: 4,
        type: "deduct",
      }),
      makeSimpleRow("Fee balance", balanceFee, {
        step: 5,
        type: "total",
        highlight: true,
      }),
    ];
  })();

  const discountSteps = [
    makeSimpleRow("Student discount", studentDiscount, {
      step: 1,
      type: "open",
    }),
    makeSimpleRow("Less: Paid", discountPaid, {
      step: 2,
      type: "deduct",
    }),
    makeSimpleRow("Discount balance", discountBalance, {
      step: 3,
      type: "total",
      highlight: true,
    }),
  ];

  const commissionSteps = [
    makeSimpleRow("Expected commission", commissionExpectedDisplay, {
      step: 1,
      type: "open",
    }),
    makeSimpleRow("Less: Commission received", commissionReceived, {
      step: 2,
      type: "deduct",
    }),
  ];

  let commissionStep = 3;
  if (discountFromReceivedCommission > 0.009) {
    commissionSteps.push(
      makeSimpleRow(
        "Less: Discount from commission",
        discountFromReceivedCommission,
        {
          step: commissionStep++,
          type: "deduct",
          sublabel: "Paid from received commission",
        },
      ),
      makeSimpleRow("Net commission received", netCommissionReceived, {
        step: commissionStep++,
        type: "subtotal",
      }),
    );
  }

  if (outstandingCommission > 0.009) {
    commissionSteps.push(
      makeSimpleRow("Outstanding commission", outstandingCommission, {
        step: commissionStep++,
        type: "subtotal",
        sublabel: "Not yet received from provider",
      }),
    );
  }

  const bonusSteps =
    bonusExpected > 0 || bonusReceived > 0
      ? [
          makeSimpleRow("Expected bonus", bonusExpected, {
            step: 1,
            type: "open",
          }),
          makeSimpleRow("Less: Bonus received", bonusReceived, {
            step: 2,
            type: "deduct",
          }),
          makeSimpleRow(
            "Bonus balance",
            Math.max(bonusExpected - bonusReceived, 0),
            {
              step: 3,
              type: "total",
              highlight: true,
            },
          ),
        ]
      : null;

  const inquiryFeeSteps = [];
  let inquiryStep = 1;

  if (
    applicationFeePlanned > 0 ||
    applicationFeeInvoiced > 0 ||
    applicationFeePaid > 0
  ) {
    const appTarget =
      applicationFeePlanned > 0
        ? applicationFeePlanned
        : applicationFeeInvoiced;
    inquiryFeeSteps.push(
      makeSimpleRow("Application fee", appTarget, {
        step: inquiryStep++,
        type: "open",
      }),
      makeSimpleRow("Less: Application fee paid", applicationFeePaid, {
        step: inquiryStep++,
        type: "deduct",
      }),
      makeSimpleRow(
        "Application fee balance",
        Math.max(appTarget - applicationFeePaid, 0),
        {
          step: inquiryStep++,
          type: "total",
          highlight: true,
        },
      ),
    );
  }

  if (
    materialFeePlanned > 0 ||
    materialFeeInvoiced > 0 ||
    materialFeePaid > 0
  ) {
    const matTarget =
      materialFeePlanned > 0 ? materialFeePlanned : materialFeeInvoiced;
    inquiryFeeSteps.push(
      makeSimpleRow("Material fee", matTarget, {
        step: inquiryStep++,
        type: "open",
      }),
      makeSimpleRow("Less: Material fee paid", materialFeePaid, {
        step: inquiryStep++,
        type: "deduct",
      }),
      makeSimpleRow(
        "Material fee balance",
        Math.max(matTarget - materialFeePaid, 0),
        {
          step: inquiryStep++,
          type: "total",
          highlight: true,
        },
      ),
    );
  }

  const compactFeesRows = [];

  if (
    applicationFeePlanned > 0 ||
    applicationFeeInvoiced > 0 ||
    applicationFeePaid > 0
  ) {
    const appTarget =
      applicationFeePlanned > 0
        ? applicationFeePlanned
        : applicationFeeInvoiced;
    compactFeesRows.push({
      id: "application_fee",
      label: "Application fee",
      total: formatCurrency(appTarget, displayCurrency),
      paid: formatCurrency(applicationFeePaid, displayCurrency),
      balance: formatCurrency(
        Math.max(appTarget - applicationFeePaid, 0),
        displayCurrency,
      ),
    });
  }

  if (
    materialFeePlanned > 0 ||
    materialFeeInvoiced > 0 ||
    materialFeePaid > 0
  ) {
    const matTarget =
      materialFeePlanned > 0 ? materialFeePlanned : materialFeeInvoiced;
    compactFeesRows.push({
      id: "material_fee",
      label: "Material fee",
      total: formatCurrency(matTarget, displayCurrency),
      paid: formatCurrency(materialFeePaid, displayCurrency),
      balance: formatCurrency(
        Math.max(matTarget - materialFeePaid, 0),
        displayCurrency,
      ),
    });
  }

  const ledger = buildUnifiedPaymentLedger({
    tuitionSteps,
    discountSteps,
    commissionSteps,
    bonusSteps,
    inquiryFeeSteps,
    commissionHeader: {
      label: "Expected commission",
      amount: formatCurrency(expectedCommissionAfterDiscount, displayCurrency),
    },
  });

  return {
    currency: displayCurrency,
    taxLabels,
    ledger,
    sections: [
      {
        id: "tuition",
        title: "Tuition fee",
        steps: tuitionSteps,
        simpleAmounts: true,
      },
      {
        id: "discount",
        title: "Student discount",
        steps: discountSteps,
        simpleAmounts: true,
      },
      {
        id: "commission",
        title: "Commission",
        steps: commissionSteps,
        simpleAmounts: true,
      },
      ...(bonusSteps
        ? [
            {
              id: "bonus",
              title: "Bonus",
              steps: bonusSteps,
              gstPairColumns: true,
            },
          ]
        : []),
      ...(inquiryFeeSteps.length
        ? [
            {
              id: "inquiry_fees",
              title: "Application & material fees",
              steps: inquiryFeeSteps,
              simpleAmounts: true,
            },
          ]
        : []),
    ],
    tuition: {
      planned: formatCurrency(commissionableTuitionFee, displayCurrency),
      paid: formatCurrency(tuitionPaid, displayCurrency),
      remaining: formatCurrency(remainingTuition, displayCurrency),
      paidRaw: tuitionPaid,
      plannedRaw: commissionableTuitionFee,
    },
    discount: {
      rows: discountSteps,
    },
    commission: {
      expected: {
        label: "Expected commission",
        exGst: formatCurrency(expectedExGst, displayCurrency),
        exclusive: formatCurrency(expectedExclusive, displayCurrency),
        inclusive: formatCurrency(expectedInclusive, displayCurrency),
        withGst: formatCurrency(expectedWithGst, displayCurrency),
      },
      rows: commissionSteps.slice(3),
      receivedRaw: commissionReceived,
      expectedRaw: commissionExpectedDisplay,
    },
    bonus:
      bonusExpected > 0 || bonusReceived > 0
        ? {
            expected: formatCurrency(bonusExpected, displayCurrency),
            received: formatCurrency(bonusReceived, displayCurrency),
          }
        : null,
    feesCard:
      compactFeesRows.length > 0
        ? {
            title: "Application & material fees",
            rows: compactFeesRows,
          }
        : null,
  };
}

/** @deprecated Use buildPlanPaymentSummaryViewModel — kept for compatibility. */
export function buildPlanPaymentSummaryItems(summary, currency = "AUD") {
  const vm = buildPlanPaymentSummaryViewModel(summary, {}, currency);
  if (!vm) return [];
  return [
    {
      label: "Tuition paid",
      value: vm.tuition.paid,
      hint: `of ${vm.tuition.planned}`,
    },
    { label: "Remaining tuition", value: vm.tuition.remaining },
    ...vm.discount.rows.map((row) => ({
      label: row.label,
      value: row.withGst,
    })),
    {
      label: "Commission expected",
      value: vm.commission.expected.withGst,
    },
    ...vm.commission.rows.map((row) => ({
      label: row.label,
      value: row.withGst,
      accent: row.highlight,
    })),
  ].filter((item) => item.value);
}
